// src/hooks/useRecentlyViewedPatients.js
//
// Tracks the last 5 patients a clinician opened from the dashboard tables,
// persisted to sessionStorage so the list survives in-tab navigation (e.g.
// open a patient, press Back, the list is still there) but is scoped to the
// browser tab and auto-cleared on sign-out via clearDashboardViewState().
//
// WHY THIS EXISTS
// ---------------
// Clinicians working across multiple patients must re-search or re-filter
// every time they return to the dashboard. This hook powers a "Recently
// viewed" dropdown that lets them jump straight back to recent records.
//
// STORAGE MODEL
// -------------
// Each entry stores a lightweight snapshot of the patient at the time they
// were viewed: { patientId, firstName, lastName, assessmentModule, viewedAt }.
// We store the snapshot rather than just the ID because patientsData is
// scoped to the clinician's current caseload and a reassigned patient would
// produce a broken lookup. The snapshot approach is consistent with how
// getPatientOverviewNavState already passes names as router location state.
//
// viewedAt is a Unix-ms number (Date.now()), not a Date object, to avoid
// the serialisation overhead of useSessionState's date replacer/reviver.
//
// DEDUPLICATION & ORDERING
// ------------------------
// Viewing a patient already in the list promotes it to position 0 and
// refreshes its name/module snapshot (handles the rare case where a name was
// corrected since the last view). The list is capped at MAX_RECENT entries.

import { useCallback } from 'react';
import useSessionState from './useSessionState';
import { DASHBOARD_KEYS } from '../utils/dashboardViewState';

/** Maximum number of recently-viewed entries to retain. */
const MAX_RECENT = 5;

/**
 * Hook that manages the recently-viewed patients list.
 *
 * @returns {{ recentPatients: Array, recordPatientView: (patient: object) => void }}
 *   - recentPatients: ordered array (most-recent first), each entry has
 *     { patientId, firstName, lastName, assessmentModule, viewedAt }.
 *   - recordPatientView: call with a patient summary object from the dashboard
 *     table row. Must include patientId, firstName, lastName, assessmentModule.
 */
export default function useRecentlyViewedPatients() {
  const [recentPatients, setRecentPatients] = useSessionState(
    DASHBOARD_KEYS.recentlyViewed,
    () => [],
  );

  // Records a patient view: deduplicates by patientId, prepends the new entry
  // to position 0, trims to MAX_RECENT, and persists via useSessionState.
  const recordPatientView = useCallback((patient) => {
    if (!patient?.patientId) return;

    setRecentPatients((prev) => {
      // Remove any existing entry for this patient (dedup)
      const filtered = prev.filter((entry) => entry.patientId !== patient.patientId);

      // Build the new entry with a fresh snapshot + timestamp
      const newEntry = {
        patientId: patient.patientId,
        firstName: patient.firstName || '',
        lastName: patient.lastName || '',
        assessmentModule: patient.assessmentModule || 'adultAdhd',
        viewedAt: Date.now(),
      };

      // Prepend and trim to the max
      return [newEntry, ...filtered].slice(0, MAX_RECENT);
    });
  }, [setRecentPatients]);

  return { recentPatients, recordPatientView };
}
